import React, { useEffect, useMemo, useState } from "react";
import logo from "../../new_logo.png";
import classes from "./header.module.css";
import util from "../../styles/utilities.module.css";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import { setDefaultValues } from "../../slices/form";
import { useAppUsageLogger, useFetchPublicConfigQuery } from "../../slices/authApi";
import {
  BADGE_STORAGE_EVENT,
  getStoredBadgeMedia,
  getStoredBadgeSummaries,
} from "../../utils/credentialingStorage";


const Header = ({ isLoggedin, logout, userGroup }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const logAppUse = useAppUsageLogger();
  const location = useLocation();

  // Fetch public config to determine if credentialing link should display
  const { data: publicConfigData } = useFetchPublicConfigQuery();
  const publicConfig = publicConfigData?.config || {};
  const showCredentialing = (() => {
    const v = publicConfig?.show_credentialing;

    if (v === undefined || v === null) return false; // default hidden unless explicitly enabled
    if (typeof v === 'boolean') return v;
    const s = String(v).toLowerCase();
    return ["1","true","t","yes","y","on"].includes(s);
  })();

  const [badgeSummaries, setBadgeSummaries] = useState(() => getStoredBadgeSummaries());
  const [badgeMedia, setBadgeMedia] = useState(() => getStoredBadgeMedia());

  useEffect(() => {
    const handleBadgeUpdate = () => {
      setBadgeSummaries(getStoredBadgeSummaries());
      setBadgeMedia(getStoredBadgeMedia());
    };

    handleBadgeUpdate();
    window.addEventListener(BADGE_STORAGE_EVENT, handleBadgeUpdate);
    return () => window.removeEventListener(BADGE_STORAGE_EVENT, handleBadgeUpdate);
  }, []);

  const earnedBadges = useMemo(() => {
    if (!Array.isArray(badgeSummaries)) {
      return [];
    }
    return badgeSummaries.filter((badge) => badge?.id).map((badge) => ({
      id: badge.id,
      name: badge.name || 'Badge',
      media: badgeMedia?.[badge.id] || {},
    }));
  }, [badgeSummaries, badgeMedia]);


  const homeIconClickHandler = () => {
    const source = (location?.pathname)?.substring(1);
    localStorage.setItem('preserveData', null);
    logAppUse(source, "tool_selection");
    dispatch(setDefaultValues());
    navigate("/");
  };

  const badgeIconClickHandler = (badgeId) => {
    if (!badgeId) {
      return;
    }
    navigate('/credentialing', { state: { focusBadgeId: badgeId } });
  };


  return (
    <div className={classes.layoutWrapper}>
      <header
        className={`${classes.headerContainer} ${!isLoggedin ? classes.headerLoggedOut : ""}`}
      >
        <div className={classes.leftGroup}>
          {isLoggedin && (
            <>
              <div className={classes.home}>
                <button className={util['btn-icon']} onClick={homeIconClickHandler} aria-label="Home">
                  <FontAwesomeIcon icon={faHome} title="Home" />
                </button>
              </div>
              {userGroup?.startsWith("cy-") && showCredentialing && (
                <>
                  <Link
                    to="/credentialing"
                    className={classes.credentialingLink}
                    aria-label="Credentialing Dashboard"
                  >
                    Credentialing
                  </Link>
                  {earnedBadges.length > 0 && (
                    <div className={classes.badgeIconStrip} role="list" aria-label="Earned badges">
                      {earnedBadges.map(({ id, name, media }) => {
                        const iconSrc = media?.iconUrl || media?.icon_url || media?.icon || null;
                        return (
                          <button
                            key={id}
                            type="button"
                            className={classes.badgeIconButton}
                            onClick={() => badgeIconClickHandler(id)}
                            title={`View badge: ${name}`}
                            aria-label={`View badge ${name}`}
                          >
                            {iconSrc ? (
                              <img
                                src={iconSrc}
                                alt={`${name} badge icon`}
                                className={classes.badgeIconImage}
                              />
                            ) : (
                              <span className={classes.badgeIconFallback} role="img" aria-label={`${name} badge icon`}>
                                🏅
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <Link to="/" className={classes.logoWrapper}>
          <img
            src={logo}
            alt="Company Logo"
            className={classes.logo}
            style={isLoggedin ? {} : { maxWidth: "100px" }}
          />
        </Link>

        <div className={classes.logout}>
          {isLoggedin && (
            <button className={util['btn-icon']} onClick={logout} aria-label="Logout">
              <FontAwesomeIcon icon={faSignOutAlt} title="Logout" />
            </button>
          )}
        </div>
      </header>
      <main
        className={classes.mainContent}
        role="main"
        data-testid="app-main-content"
      >
        <Outlet />
      </main>
      <footer className={classes.footer}>
        {location.pathname !== "/" &&<p style={{ fontSize: '12px' }}>Content on this site has been dynamically generated by artificial intelligence and may contain inaccurate information. Always verify before using or distributing.</p>}
        {userGroup?.startsWith("cy-") && (
        <div style={{ maxWidth: '1120px', margin: '0 auto' }}>
          <p style={{ fontSize: '12px', textAlign: 'center' }}>By creating an account and using the tool, I acknowledge that all information entered will become part of the product's model. I agree not to enter any Personally Identifiable Information (PII) about myself or my students and to adhere to City Year's policies on Protected or Sensitive information.</p>
        </div>)}
        {userGroup?.startsWith("bt-") && (
        <div style={{ maxWidth: '1120px', margin: '0 auto' }}>
          <p style={{ fontSize: '12px', textAlign: 'center' }}>Acknowledgment of Limited Pilot License. By accessing the ClassGenius platform, you acknowledge and agree that unless Breakthrough Central Texas ("Breakthrough") proceeds to Phase 2 of the Pilot, the platform may only be used to create supplementary materials for Breakthrough's 2025 Middle School Summer Academy science curriculum. Any materials generated using the platform may only be used during the 2025 Summer Academy. Your access is provided solely for the duration of this Pilot and does not grant any rights to continued use of the platform or its outputs beyond the 2025 Summer Academy.</p>
        </div>)}
        <br/>
        <p>&copy; Copyright 2024-2025 ClassGenius - All Rights Reserved</p>
        <Link className={classes.link} to="/privacy">
          Privacy Policy
        </Link>
        <p className={classes.separator}> - </p>
        <Link className={classes.link} to="/terms">
          Terms of Use
        </Link>
      </footer>
    </div>
  );
};

export default Header;
